import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { FormulaRenderer } from './FormulaRenderer';

const SYMBOL_GROUPS = [
  {
    label: 'Exposants / Indices',
    items: [
      { label: 'x²', insert: '$x^{2}$' },
      { label: 'xⁿ', insert: '$x^{n}$' },
      { label: 'x₁', insert: '$x_{1}$' },
      { label: '10ⁿ', insert: '$10^{n}$' },
    ]
  },
  {
    label: 'Fractions / Racines',
    items: [
      { label: 'a/b', insert: '$\\frac{a}{b}$' },
      { label: '√x', insert: '$\\sqrt{x}$' },
    ]
  },
  {
    label: 'Grec',
    items: [
      { label: 'α', insert: '$\\alpha$' },
      { label: 'β', insert: '$\\beta$' },
      { label: 'γ', insert: '$\\gamma$' },
      { label: 'δ', insert: '$\\delta$' },
      { label: 'Δ', insert: '$\\Delta$' },
      { label: 'λ', insert: '$\\lambda$' },
      { label: 'π', insert: '$\\pi$' },
      { label: 'σ', insert: '$\\sigma$' },
      { label: 'Ω', insert: '$\\Omega$' },
      { label: 'μ', insert: '$\\mu$' },
    ]
  },
  {
    label: 'Symboles',
    items: [
      { label: '×', insert: '$\\times$' },
      { label: '÷', insert: '$\\div$' },
      { label: '±', insert: '$\\pm$' },
      { label: '≤', insert: '$\\leq$' },
      { label: '≥', insert: '$\\geq$' },
      { label: '≠', insert: '$\\neq$' },
      { label: '≈', insert: '$\\approx$' },
      { label: '∞', insert: '$\\infty$' },
      { label: '→', insert: '$\\rightarrow$' },
      { label: '⇌', insert: '$\\rightleftharpoons$' },
    ]
  },
  {
    label: 'Chimie',
    items: [
      { label: 'H₂O', insert: '$\\ce{H2O}$' },
      { label: 'CO₂', insert: '$\\ce{CO2}$' },
      { label: 'Fe³⁺', insert: '$\\ce{Fe^{3+}}$' },
      { label: 'SO₄²⁻', insert: '$\\ce{SO4^{2-}}$' },
      { label: '→', insert: '$\\ce{->}$' },
      { label: '⇌', insert: '$\\ce{<=>}$' },
      { label: '(aq)', insert: '$\\ce{(aq)}$' },
      { label: '(g)', insert: '$\\ce{(g)}$' },
      { label: '(s)', insert: '$\\ce{(s)}$' },
      { label: '(l)', insert: '$\\ce{(l)}$' },
      { label: 'réaction', insert: '$\\ce{A + B -> C + D}$' },
      { label: 'mol·L⁻¹', insert: '$mol \\cdot L^{-1}$' },
    ]
  },
  {
    label: 'Modèles',
    items: [
      { label: 'E=mc²', insert: '$E = mc^{2}$' },
      { label: 'f(x)', insert: '$f(x) = $' },
      { label: 'kg·m⁻³', insert: '$kg \\cdot m^{-3}$' },
      { label: 'Δt', insert: '$\\Delta t$' },
      { label: '∫', insert: '$\\int_{a}^{b} f(x)\\,dx$' },
      { label: 'd/dt', insert: '$\\frac{d}{dt}$' },
      { label: 'ln(x)', insert: '$\\ln(x)$' },
      { label: 'Σ', insert: '$\\sum_{i=1}^{n}$' },
    ]
  }
];

/** Wraps a Textarea or Input with a Σ formula insertion button. Desktop only. */
export function FormulaInput({ value, onChange, multiline = false, placeholder, className = '', testId, showPreview = false, sciMode = false }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) { onChange(value + text); setOpen(false); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const newVal = value.slice(0, start) + text + value.slice(end);
    onChange(newVal);
    setOpen(false);
    setTimeout(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    }, 50);
  };

  const handleKeyDown = (e) => {
    // Ctrl+M / Cmd+M: insert empty formula template
    if ((e.ctrlKey || e.metaKey) && e.key === 'm') {
      e.preventDefault();
      const el = inputRef.current;
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      const template = '$$';
      const newVal = value.slice(0, start) + template + value.slice(end);
      onChange(newVal);
      setTimeout(() => {
        el?.focus();
        const pos = start + 1; // cursor between the two $
        el?.setSelectionRange(pos, pos);
      }, 50);
    }
  };

  const hasFormula = value && value.includes('$');
  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="relative w-full">
      <InputComponent
        ref={inputRef}
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={sciMode ? handleKeyDown : undefined}
        className={`${className} ${!isMobile && sciMode ? 'pr-9' : ''}`}
      />
      {/* Σ button — desktop only, visible only in science mode */}
      {!isMobile && sciMode && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testId ? `${testId}-formula-btn` : undefined}
              className="absolute right-2 top-2 w-6 h-6 flex items-center justify-center rounded text-xs font-bold text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
              title="Insérer une formule (Ctrl+M)"
            >
              Σ
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3 bg-[#0d1117] border-white/10" align="end" side="bottom">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Insérer une formule</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {SYMBOL_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">{group.label}</p>
                  <div className="flex flex-wrap gap-1">
                    {group.items.map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => insertAtCursor(item.insert)}
                        className="px-2 py-1 rounded text-xs bg-white/5 hover:bg-blue-500/15 hover:text-blue-400 transition-colors border border-white/5"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-white/5">
              <p className="text-[10px] text-gray-600">Syntaxe : entourez de <code className="text-blue-400">$...$</code> pour une formule. Ex: <code className="text-blue-400">$E = mc^{'{2}'}$</code></p>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {/* Live preview — desktop only, science mode, if content has formulas */}
      {showPreview && sciMode && !isMobile && hasFormula && (
        <div className="mt-1.5 px-3 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.04] text-sm" data-testid={testId ? `${testId}-preview` : undefined}>
          <FormulaRenderer text={value} />
        </div>
      )}
    </div>
  );
}
